import React, { useEffect, useState, useTransition } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import FormFields from '../../components/FormComponents/FormFields';
import Consent from '../CommunicationConsent';
import "./index.css";

const Offers = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [offerFail, setOfferFail] = useState(false);
  const [hashKey, setHashKey] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [showConsentSection, setShowConsentSection] = useState(false);
  const [consent, setConsent] = useState(true);
  const [scheduled, setScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [formValues, setFormValues] = useState(null);
  const [formKey, setFormKey] = useState(0);
  
  // Form state for verification
  const [verificationValues, setVerificationValues] = useState({
    LastName: '',
    FileNumber: ''
  });
  
  // Verified user data - now includes all fields from hashkey
  const [verifiedData, setVerifiedData] = useState({
    firstName: '',
    lastName: '',
    fileNumber: '',
    propState: '',
    email: '',
    phone: ''
  });
  
  // Form errors state
  const [formErrors, setFormErrors] = useState({});
  
  // Track if field has been touched
  const [touched, setTouched] = useState({
    LastName: false,
    FileNumber: false
  });
  
  const [isPending, startTransition] = useTransition();

  // Format phone number to (XXX) XXX-XXXX format
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    
    return phone; // Return original if not 10 digits
  };

  // Handle hashkey from URL parameter - ONLY pre-fills form, does NOT auto-submit
  useEffect(() => {
    const key = searchParams.get('key');
    
    if (key) {
      const apiEndpoint = process.env.REACT_APP_SUNSOFT_API_GET_SESSION_KEY_DECRYPT;
      
      if (!apiEndpoint) {
        console.error("REACT_APP_SUNSOFT_API_GET_SESSION_KEY_DECRYPT is not defined in environment variables!");
        return;
      }
      
      setIsLoading(true);
      
      axios
        .post(
          apiEndpoint,
          { key: key },
          {
            headers: { "Content-Type": "application/json" },
          }
        )
        .then((response) => {
          let userData = response.data.parameters;
          
          // Extract user data from response
          let firstName = '';
          let lastName = '';
          
          if (userData.borrowerName) {
            let name = userData.borrowerName.split(" ");
            firstName = name[0];
            lastName = name.slice(1).join(" ");
          } else {
            if (userData.borrowerFirstName) {
              firstName = userData.borrowerFirstName;
            }
            if (userData.borrowerLastName) {
              lastName = userData.borrowerLastName;
            }
          }
          
          const loanNumber = userData.loanNumber || '';
          
          // Pre-fill verification form (Step 1)
          setVerificationValues({
            LastName: lastName || '',
            FileNumber: loanNumber
          });
          
          // Store data for later use after manual verification (Step 2)
          setVerifiedData({
            firstName: firstName || '',
            lastName: lastName || '',
            fileNumber: loanNumber,
            propState: '', // Will be filled after verification
            email: userData.emailAddress || '',
            phone: formatPhoneNumber(userData.borrowerPhone || '')
          });
          
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Session key API error:", error.response?.data || error.message);
          setIsLoading(false);
        });
    }
  }, [searchParams]);

  // Generate fallback hash (keep existing logic for non-hashkey scenarios)
  useEffect(() => {
    // Only generate fallback hash if no key parameter exists
    const key = searchParams.get('key');
    if (key) return;
    
    const digestMessage = async (message) => {
      try {
        if (window.crypto && window.crypto.subtle) {
          const msgUint8 = new TextEncoder().encode(message);
          const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
          return hashHex;
        } else {
          console.warn("Web Crypto API not available - using fallback hash");
          let hash = 0;
          for (let i = 0; i < message.length; i++) {
            const char = message.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
          }
          return Math.abs(hash).toString(16).padStart(64, '0');
        }
      } catch (error) {
        console.error("Error generating hash:", error);
        return Date.now().toString(16).padStart(64, '0');
      }
    };
  
    const today = new Date();
    const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
    digestMessage(time).then((val) => setHashKey(val));
  }, [searchParams]);

  useEffect(() => {
    if (hashKey) {
      try {
        sessionStorage.setItem('offer_token', hashKey);
        sessionStorage.setItem('offer_token_expire', 'false');
      } catch (error) {
        console.error("Session storage error:", error);
      }
    }
  }, [hashKey]);

  // Validate verification form
  const validateVerificationForm = (values) => {
    const errors = {};
    
    if (!values.LastName) {
      errors.LastName = 'Last Name is required';
    }
    
    if (!values.FileNumber) {
      errors.FileNumber = 'File Number is required';
    } else if (values.FileNumber.length !== 12) {
      errors.FileNumber = 'Must be 12 characters';
    }
    
    return errors;
  };

  // Handle verification input change
  const handleVerificationChange = (e) => {
    const { name, value } = e.target;
    setVerificationValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle verification input blur
  const handleVerificationBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    setFormErrors(validateVerificationForm(verificationValues));
  };
  
  // Handle verification submit - This is called when user clicks Submit button
  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateVerificationForm(verificationValues);
    setFormErrors(errors);
    
    setTouched({
      LastName: true,
      FileNumber: true
    });
    
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsLoading(true);
    setOfferFail(false);

    try {
      const requestBody = {
        lastName: verificationValues.LastName,
        loanNumber: verificationValues.FileNumber
      };

      const response = await axios.post(
        'https://m3h44ji38i.execute-api.us-west-1.amazonaws.com/prod/swmc-offer-check-loan',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Extract property state from response
      let propState = '';
      if (response.data && response.data.loan && response.data.loan.propState) {
        if (Array.isArray(response.data.loan.propState)) {
          propState = response.data.loan.propState[0];
        } else {
          propState = response.data.loan.propState;
        }
      }

      // Check for success response
      if (response.data && response.data.loan && 
          response.data.loan.status && 
          response.data.loan.status[0] === "SUCCESS") {
        
        // Merge verified data with property state
        setVerifiedData(prev => ({
          ...prev,
          lastName: verificationValues.LastName,
          fileNumber: verificationValues.FileNumber,
          propState: propState
        }));

        // Store in sessionStorage
        sessionStorage.setItem('validated_last_name', verificationValues.LastName);
        sessionStorage.setItem('validated_file_number', verificationValues.FileNumber);
        sessionStorage.setItem('validated_prop_state', propState);
        
        setIsVerified(true);
        setIsLoading(false);
        
        // Scroll to congratulations message with 100px padding from top
        setTimeout(() => {
          const element = document.getElementById('details-form-section');
          if (element) {
            const topPosition = element.offsetTop - 100;
            window.scrollTo({ top: topPosition, behavior: 'smooth' });
          }
        }, 300);
      } else {
        setOfferFail(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setOfferFail(true);
      setIsLoading(false);
    }
  };

  // Time and date helper functions
  function tConvert(time) {
    time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
    if (time.length > 1) {
      time = time.slice(1);
      time[5] = +time[0] < 12 ? ' AM' : ' PM';
      time[0] = +time[0] % 12 || 12;
    }
    return time.join('');
  }

  const FormatChanger = (date) => {
    let datearray = date.split("-");
    let newdate = datearray[1] + '/' + datearray[2] + '/' + datearray[0];
    return newdate;
  };

  const disablePastDate = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return yyyy + "-" + mm + "-" + dd;
  };

  const goBackToFirstPage = () => {
    setShowConsentSection(false);
    
    // Increment key to force FormFields to remount with saved values
    setFormKey(prev => prev + 1);
    
    // Scroll back to the form section
    setTimeout(() => {
      const element = document.getElementById('details-form-section');
      if (element) {
        const topPosition = element.offsetTop - 100;
        window.scrollTo({ top: topPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleConsentCheck = (hasConsent) => {
    setConsent(hasConsent);
  };

  // Define form fields for details form
  const getFormFields = () => {
    return [
      {
        name: "FirstName",
        type: "text",
        placeholder: "First Name*",
        required: true,
        validations: ["required"],
        colProps: { sm: 12 }
      },
      {
        name: "LastName",
        type: "text",
        placeholder: "Last Name*",
        required: true,
        validations: ["required"],
        colProps: { sm: 12 }
      },
      {
        name: "FileNumber",
        type: "text",
        placeholder: "File Number*",
        required: true,
        validations: ["required"],
        colProps: { sm: 12 }
      },
      {
        name: "PropertyState",
        type: "text",
        placeholder: "Property State*",
        required: true,
        validations: ["required"],
        colProps: { sm: 12 }
      },
      {
        name: "PhoneNumber",
        type: "phone",
        placeholder: "Call Me At*",
        required: true,
        validations: ["required", "phone"],
        colProps: { sm: 12 }
      },
      {
        name: "Email",
        type: "email",
        placeholder: "Enter Email*",
        required: true,
        validations: ["required", "email"],
        colProps: { sm: 12 }
      },
      {
        name: "Date",
        type: "date",
        placeholder: "",
        required: true,
        validations: ["required"],
        min: disablePastDate(),
        colProps: { sm: 8},
        className: "date-input"
      },
      {
        name: "Time",
        type: "time",
        placeholder: "",
        required: true,
        validations: ["required"],
        colProps: { sm: 4 },
        className: "time-input"
      },
      {
        name: "Comments",
        type: "textarea",
        placeholder: "Comments",
        colProps: { sm: 12 }
      }
    ];
  };

  // Handle details form submit
  const handleDetailsSubmit = async (formData) => {
    if (!showConsentSection) {
      setFormValues(formData);
      setShowConsentSection(true);
      
      setTimeout(() => {
        const consentElement = document.getElementById('consent-section');
        if (consentElement) {
          const topPosition = consentElement.offsetTop - 100;
          window.scrollTo({
            top: topPosition,
            behavior: 'smooth'
          });
        }
      }, 200);
      return;
    }
  
    const newData = { ...(formValues || {}) };
    newData.TCPA_Consent_Given = consent ? 'Yes' : 'No';
  
    setIsLoading(true);
  
    try {
      const apiFormData = new FormData();
      apiFormData.append("postData", JSON.stringify(newData));
      apiFormData.append('leadSource', 'NET007');
  
      // First API call
      try {
        const requestURL = process.env.REACT_APP_OFFER_BORROWER_MAIL;
        const borrowerResponse = await fetch(requestURL, {
          method: 'POST',
          body: apiFormData
        });
        
        if (!borrowerResponse.ok) {
          console.error("Borrower mail API error:", borrowerResponse.status);
        }
      } catch (borrowerError) {
        console.error("Error sending borrower mail:", borrowerError);
      }
  
      // Second API call
      try {
        const dbrequestURL = process.env.REACT_APP_OFFER_SWMC_MAIL;
        
        const swmcFormData = new FormData();
        swmcFormData.append("postData", JSON.stringify(newData));
        swmcFormData.append('leadSource', 'NET007');
        
        const swmcResponse = await fetch(dbrequestURL, {
          method: 'POST',
          body: swmcFormData
        });
        
        if (!swmcResponse.ok) {
          console.error("SWMC mail API error:", swmcResponse.status);
          throw new Error(`SWMC API responded with status ${swmcResponse.status}`);
        }
      } catch (swmcError) {
        console.error("Error sending SWMC mail:", swmcError);
      }
  
      if (newData.Date) {
        setScheduledDate(FormatChanger(newData.Date));
      } else {
        setScheduledDate("your selected date");
      }
      
      if (newData.Time) {
        setScheduledTime(tConvert(newData.Time));
      } else {
        setScheduledTime("your selected time");
      }
      
      setScheduled(true);
      setShowConsentSection(false);
      
      // Scroll to thank you message
      setTimeout(() => {
        const element = document.getElementById('thank-you-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
      
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting your request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to start over
  const handleStartOver = () => {
    setIsVerified(false);
    setScheduled(false);
    setShowConsentSection(false);
    setVerificationValues({ LastName: '', FileNumber: '' });
    setVerifiedData({ 
      firstName: '',
      lastName: '', 
      fileNumber: '', 
      propState: '',
      email: '',
      phone: ''
    });
    setFormValues(null);
    setOfferFail(false);
    setTouched({ LastName: false, FileNumber: false });
    setFormErrors({});
    setFormKey(0);
    
    // Clear session storage
    sessionStorage.removeItem('validated_last_name');
    sessionStorage.removeItem('validated_file_number');
    sessionStorage.removeItem('validated_prop_state');
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
 
  const InlineLoader = () => (
    <div className="loader-container">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <article className="offers-page">
      <Helmet>
        <title>LowRates.com by Sun West Mortgage Company, Inc. NMLS 3277 - LowRates Offer</title>
        <meta name="description" content="LowRates offers residential home loan mortgages such as purchase, refinance, property rehabilitation loan, and reverse mortgage." />
        <meta property="og:url" content="https://www.lowrates.com/offer" />
        <meta property="og:title" content="LowRates.com by Sun West Mortgage Company, Inc. NMLS 3277 - LowRates Offer" />
        <meta property="og:description" content="LowRates offers residential home loan mortgages such as purchase, refinance, property rehabilitation loan, and reverse mortgage." />
        <meta property="og:keyword" content="low mortgage rates, mortgage offers, home loan offers, purchase mortgage, refinance mortgage, mortgage refinance rates, home loan rates, low interest home loans, competitive mortgage rates, residential mortgage loans, mortgage financing options, apply for mortgage online, compare mortgage rates, FHA home loans, VA mortgage loans, jumbo mortgage rates, first time home buyer mortgage, homeowner refinance offers, trusted mortgage lender, LowRates.com mortgage" />
        <meta property="og:brand" content="LowRates.com by Sun West Mortgage Company, Inc. NMLS 3277 - LowRates Offer" />
        <link rel="preload" href="https://resources.swmc.com/swmc-react-2025/Swmc-offer/swmc-offer.webp" as="image" type="image/webp" fetchPriority="high" />
      </Helmet>

      <div className="offers-wrapper">
        <div className={isVerified ? "hero_image_SwmcCong" : "hero_image_SwmcOffer"}>
          <div className="hero-content">
            <h1>Offer</h1>
          </div>
        </div>

        <Container fluid className={isVerified ? "pageContainer_SwmcCong" : "pageContainer_SwmcOffer"}>
          
          {/* Verification Section */}
          {!isVerified && !scheduled && (
            <div className="content-section">
              <h3>Have Us Call You!</h3>
              <p className="info-text_SwmcOffer">
                If you have received an offer from LowRates, please enter the information from the email/mailer below to access the offer and request a call back from one of our licensed specialists:
              </p>

              {offerFail && (
                <div className="error-message_SwmcOffer">
                  <p>We could not verify your file number</p>
                  <p>Please make sure you are entering in the file number and last name as printed on the flyer</p>
                </div>
              )}

              {isLoading ? (
                <InlineLoader />
              ) : (
                <div className="form-wrapper_SwmcOffer">
                  <div className="form-containerDiv">
                    <form className="offer-form" onSubmit={handleVerificationSubmit}>
                      <Row>
                        <Col sm={12}>
                          <div className="form-group">
                            <input
                              type="text"
                              name="LastName"
                              value={verificationValues.LastName}
                              onChange={handleVerificationChange}
                              onBlur={handleVerificationBlur}
                              className={`form-control ${formErrors.LastName && touched.LastName ? 'is-invalid' : ''}`}
                              placeholder="Last Name*"
                            />
                            {formErrors.LastName && touched.LastName && (
                              <div className="invalid-feedback">{formErrors.LastName}</div>
                            )}
                          </div>
                        </Col>
                        <Col sm={12} className="mt-3">
                          <div className="form-group">
                            <input
                              type="text"
                              name="FileNumber"
                              value={verificationValues.FileNumber}
                              onChange={handleVerificationChange}
                              onBlur={handleVerificationBlur}
                              className={`form-control ${formErrors.FileNumber && touched.FileNumber ? 'is-invalid' : ''}`}
                              placeholder="File Number*"
                            />
                            {formErrors.FileNumber && touched.FileNumber && (
                              <div className="invalid-feedback">{formErrors.FileNumber}</div>
                            )}
                          </div>
                        </Col>
                        <Col sm={12} className="mt-3">
                          <button type="submit" className="btn btn-primary">
                            Submit
                          </button>
                        </Col>
                      </Row>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Details Form Section */}
          {isVerified && !scheduled && (
            <div id="details-form-section" style={{ paddingTop: '40px' }}>
              <h3>Congratulations! Your file number has been verified</h3>
              <p style={{ fontSize: '15px', maxWidth: '600px', margin: '0 auto' }}>
                Please fill out the information below to request a call back.
              </p>

              <div id="formContainid_SwmcCong" className="formContainDiv_SwmcCong">
                {!showConsentSection && (
                  <div className="formClass_SwmcCong">
                    <FormFields
                      key={`form-${formKey}`}
                      fields={getFormFields()}
                      onSubmit={handleDetailsSubmit}
                      isLoading={isLoading}
                      hasConsent={consent}
                      onConsentChange={handleConsentCheck}
                      initialValues={{
                        FirstName: formValues?.FirstName || verifiedData.firstName || '',
                        LastName: formValues?.LastName || verifiedData.lastName || '',
                        FileNumber: formValues?.FileNumber || verifiedData.fileNumber || '',
                        PropertyState: formValues?.PropertyState || verifiedData.propState || '',
                        PhoneNumber: formValues?.PhoneNumber || verifiedData.phone || '',
                        Email: formValues?.Email || verifiedData.email || '',
                        Date: formValues?.Date || '',
                        Time: formValues?.Time || '',
                        Comments: formValues?.Comments || ''
                      }}
                    />
                  </div>
                )}
                
                {showConsentSection && (
                  <div id="consent-section" className="formClass_SwmcCong" style={{ paddingTop: '40px' }}>
                    <Consent
                      handleClick1={() => setConsent(true)}
                      handleClick2={() => setConsent(false)}
                      showConsenSection={showConsentSection}
                    />
                    <FormFields
                      key={`consent-${formKey}`}
                      fields={[]}
                      onSubmit={handleDetailsSubmit}
                      isLoading={isLoading}
                      showConsentSection={showConsentSection}
                      onGoBack={goBackToFirstPage}
                      hasConsent={consent}
                      onConsentChange={handleConsentCheck}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Thank You Section */}
          {scheduled && (
            <div id="thank-you-section" style={{ display: 'flex', justifyContent: 'center', paddingTop: '40px' }}>
              <div className="thanksDiv">
                <h2>Thank you for scheduling a call with us to discuss your mortgage needs!</h2>
                <h5>We will contact you on {scheduledDate} between {scheduledTime}</h5>
              </div>
            </div>
          )}
        </Container>
      </div>
    </article>
  );
};

export default Offers;